import { Link } from "react-router-dom";
import { useNoticiasDestacadas } from "../hooks/useCms";
import { mediaUrl } from "../lib/strapi";
import type { Noticia } from "../types/cms";

/**
 * Sección de noticias destacadas en la home.
 *
 * Lee noticias publicadas con `destacada=true` (hasta 3) y las muestra
 * como una rejilla compacta. Si no hay ninguna destacada o aún no hay
 * conexión con el CMS, NO se renderiza nada — la home queda como antes.
 *
 * Cada tarjeta enlaza a `/noticias/<slug>` y al final de la sección se
 * ofrece un enlace "Ver todas las noticias" hacia /noticias para que el
 * usuario pueda explorar el resto.
 */

const etiquetaLabel: Record<NonNullable<Noticia["etiquetas"]>, string> = {
  programa: "Programa",
  evento: "Evento",
  prensa: "Prensa",
  convocatoria: "Convocatoria",
};

export function NoticiasDestacadasHome() {
  const { data: noticias, isLoading } = useNoticiasDestacadas(3);

  if (isLoading) return null;
  if (!noticias || noticias.length === 0) return null;

  return (
    <section className="bg-white py-20" aria-labelledby="home-noticias-heading">
      <div className="container-emjc">
        <div className="mb-10 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div>
            <p className="text-sm font-semibold uppercase tracking-widest text-brand-orange-deep">
              Últimas novedades
            </p>
            <h2
              id="home-noticias-heading"
              className="mt-2 font-display text-4xl font-semibold md:text-5xl"
            >
              {noticias.length === 1 ? "Lo último en la EMJC." : "En la EMJC."}
            </h2>
          </div>
          <Link
            to="/noticias"
            className="text-sm font-semibold text-brand-ink hover:text-brand-orange-deep"
          >
            Ver todas las noticias →
          </Link>
        </div>

        <ul
          className={
            // Layout adaptativo: 1 noticia ocupa toda la fila, 2 → 2 columnas,
            // 3 → 3 columnas. Así una sola destacada se ve grande y bonita.
            noticias.length === 1
              ? "grid grid-cols-1"
              : noticias.length === 2
                ? "grid gap-6 md:grid-cols-2"
                : "grid gap-6 md:grid-cols-2 xl:grid-cols-3"
          }
        >
          {noticias.map((n) => (
            <li key={n.documentId ?? n.id}>
              <NoticiaDestacadaCard noticia={n} solo={noticias.length === 1} />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function NoticiaDestacadaCard({
  noticia,
  solo,
}: {
  noticia: Noticia;
  solo: boolean;
}) {
  const portadaSrc =
    mediaUrl(noticia.imagenPortada?.formats?.medium?.url) ??
    mediaUrl(noticia.imagenPortada?.url);

  const fechaTxt = new Date(noticia.fechaPublicacion).toLocaleDateString(
    "es-ES",
    { day: "numeric", month: "long", year: "numeric" },
  );

  const etiqueta = noticia.etiquetas ? etiquetaLabel[noticia.etiquetas] : null;

  return (
    <Link
      to={`/noticias/${noticia.slug}`}
      className={
        solo
          ? "group grid gap-0 overflow-hidden rounded-3xl border border-gray-300 bg-white transition hover:border-brand-orange focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2 md:grid-cols-2"
          : "group block overflow-hidden rounded-2xl border border-gray-300 bg-white transition hover:border-brand-orange focus:outline-none focus:ring-2 focus:ring-brand-orange focus:ring-offset-2"
      }
    >
      {portadaSrc && (
        <div
          className={
            solo
              ? "aspect-[4/3] w-full overflow-hidden bg-brand-paper md:aspect-auto"
              : "aspect-[16/9] w-full overflow-hidden bg-brand-paper"
          }
        >
          <img
            src={portadaSrc}
            alt={noticia.imagenPortada?.alternativeText ?? noticia.titulo}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        </div>
      )}
      <div className={solo ? "flex flex-col justify-center p-8 md:p-10" : "p-5"}>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs uppercase tracking-widest text-gray-700">
          <time dateTime={noticia.fechaPublicacion}>{fechaTxt}</time>
          {etiqueta && (
            <span className="rounded-full bg-brand-orange-soft px-2 py-0.5 text-brand-orange-deep">
              {etiqueta}
            </span>
          )}
          <span className="rounded-full bg-accent-sun px-2 py-0.5 text-brand-ink">
            Destacada
          </span>
        </div>
        <h3
          className={
            solo
              ? "mt-3 font-display text-2xl font-semibold leading-tight md:text-3xl"
              : "mt-2 font-display text-xl font-semibold leading-tight"
          }
        >
          {noticia.titulo}
        </h3>
        {noticia.resumen && (
          <p
            className={
              solo
                ? "mt-4 text-base text-gray-700"
                : "mt-3 line-clamp-3 text-sm text-gray-700"
            }
          >
            {noticia.resumen}
          </p>
        )}
        <p className="mt-4 text-sm font-semibold text-brand-orange-deep">
          Leer noticia →
        </p>
      </div>
    </Link>
  );
}
